import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { RowsPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/rows.css';
import 'photoswipe/dist/photoswipe.css';
import type { Profile, ProfilePhoto } from '@modelcast/types';
import { COUNTRIES } from '../model-signup/countries';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { ReferencePackCard, getReferencePackAccessDurationLabel } from '../reference-packs/ReferencePackCard';
import { EventCard, EventCardData } from '../../components/EventCard';
import { FollowButton } from '../follow/FollowButton';

// Upcoming session type - matches what the API returns
interface UpcomingSession {
  room: {
    id: string;
    code: string;
    name: string;
    description: string | null;
    scheduledAt: string | null;
    endsAt: string | null;
    startedAt: string | null;
    imageUrl: string | null;
    hostName: string;
    hostAvatarUrl: string | null;
    isTutored: boolean;
    maxAttendees: number | null;
    seatsConfirmed: number;
    pricingType: 'free' | 'fixed' | 'donation';
    ticketPrice: number | null;
  };
  role?: 'hosting' | 'modeling' | 'attending';
}

interface ProfileReferencePack {
  id: string;
  title: string;
  description: string | null;
  priceCents: number | null;
  currency: string;
  coverImageUrl: string | null;
  license: string | null;
  accessExpiresOn: string | null;
  accessDurationCount: number | null;
  accessDurationUnit: 'days' | 'weeks' | 'months' | 'years' | null;
  captureIds: string[];
  recordingIds: string[];
  room: {
    code: string;
    name: string;
  };
}

// Country flag emoji from country code
function getCountryFlag(code: string): string {
  const codePoints = code
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function getProfileRoles(profile: Profile) {
  const roles: string[] = [];
  if (profile.isModel) roles.push('Model');
  if (profile.isArtist) roles.push('Artist');
  if (profile.isHost) roles.push('Host');
  return roles.length > 0 ? roles.join(' / ') : 'Profile';
}

export function ProfilePage() {
  const { handle, id } = useParams<{ handle?: string; id?: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);
  const [referencePacks, setReferencePacks] = useState<ProfileReferencePack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  useEffect(() => {
    fetchProfile(handle || id);
  }, [handle, id]);

  const fetchProfile = async (profileHandle?: string) => {
    if (!profileHandle) {
      setError('No profile provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedPhotoIndex(0);
      const response = await fetch(`/api/profiles/${encodeURIComponent(profileHandle)}`);

      if (response.status === 404) {
        setError('Profile not found');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      const loadedProfile = data.profile as Profile;
      setProfile(loadedProfile);

      const sessionsResponse = await fetch(`/api/profiles/${loadedProfile.id}/sessions`);
      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json();
        setUpcomingSessions(sessionsData.sessions || []);
      } else {
        setUpcomingSessions([]);
      }

      const packsResponse = await fetch(`/api/products/reference-packs/profiles/${loadedProfile.id}`);
      if (packsResponse.ok) {
        const packsData = await packsResponse.json();
        setReferencePacks(packsData.products || []);
      } else {
        setReferencePacks([]);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-text-dim">Loading...</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex flex-col bg-bg pt-[88px]">
        <NavBar
          variant="light"
          links={[
            { label: 'Drawing Events', href: '/events' },
            { label: 'Artists', href: '/artists' },
            { label: 'Models', href: '/models', active: true },
          ]}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-panel border border-line rounded-xl p-8 max-w-md text-center">
            <h2 className="text-xl font-medium text-text mb-2">Profile not found</h2>
            <p className="text-text-dim mb-6">
              The profile you're looking for doesn't exist or may have been removed.
            </p>
            <button onClick={() => navigate('/')} className="btn btn-accent">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const countryName = profile.country
    ? COUNTRIES.find(c => c.code === profile.country)?.name
    : null;

  const heroPhotos = profile.photos.length > 0 ? profile.photos : profile.artworkPhotos;
  const primaryPhoto = heroPhotos[selectedPhotoIndex] || heroPhotos[0] || profile.spacePhotos[0];
  const firstName = profile.displayName.split(' ')[0];
  const roleLabel = getProfileRoles(profile);
  const showPortraits = profile.isModel && profile.photos.length > 1;
  const showPoseSpace = profile.isModel && profile.spacePhotos.length > 0;
  const showArtwork = profile.isArtist && profile.artworkPhotos.length > 0;
  const showReferencePacks = referencePacks.length > 0;
  const showProfileFacts = Boolean((profile.country && countryName) || profile.instagram || profile.website);

  return (
    <div className="min-h-screen flex flex-col bg-bg pt-[88px]">
      <NavBar
        variant="light"
        links={[
          { label: 'Drawing Events', href: '/events' },
          { label: 'Artists', href: '/artists' },
          { label: 'Models', href: '/models', active: true },
        ]}
      />

      {/* Hero Section */}
      <section className="pt-8 pb-0">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-14 items-end">
            <div className="flex flex-col justify-end">
              <div className="inline-flex items-center gap-2.5 text-[11px] uppercase text-text-dim mb-4">
                <span className="w-[22px] h-px bg-text-faint" />
                {roleLabel}
              </div>
              <h1 className="font-display text-[clamp(56px,9vw,120px)] font-light leading-[0.92] mb-6">
                {firstName}.
              </h1>

              {showProfileFacts && (
                <div className="mb-6 flex flex-wrap gap-7 border-y border-line py-[18px] text-sm text-text-dim">
                  {profile.country && countryName && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase text-text-faint">From</span>
                      <span className="text-[15px] text-text flex items-center gap-1.5">
                        <span>{getCountryFlag(profile.country)}</span>
                        {countryName}
                      </span>
                    </div>
                  )}
                  {profile.instagram && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase text-text-faint">Instagram</span>
                      <a
                        href={`https://instagram.com/${profile.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[15px] text-text hover:underline"
                      >
                        @{profile.instagram}
                      </a>
                    </div>
                  )}
                  {profile.website && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase text-text-faint">Website</span>
                      <a
                        href={profile.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[15px] text-text hover:underline"
                      >
                        {profile.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              )}

              <div>
                <FollowButton
                  profileId={profile.id}
                  followLabel="Follow"
                  followingLabel="Following"
                />
              </div>
            </div>

            <div className="order-first lg:order-last">
              {primaryPhoto ? (
                <div
                  className="w-full bg-cover bg-center rounded"
                  style={{
                    backgroundImage: `url(${primaryPhoto.url})`,
                    aspectRatio: '4/5',
                    maxHeight: 'calc(100vh - 140px)',
                  }}
                />
              ) : (
                <div className="w-full aspect-[4/5] max-h-[calc(100vh-140px)] bg-panel2 rounded flex items-center justify-center">
                  <span className="font-display text-[clamp(52px,8vw,96px)] text-text-faint">
                    {firstName[0]?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {profile.bio && (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-14 mt-8">
              <div className="hidden lg:block" />
              <div className="max-w-[640px]">
                {profile.bio.split('\n\n').map((paragraph, i) => (
                  <p
                    key={i}
                    className={`text-[18px] font-light leading-[1.6] text-text-dim mb-4 last:mb-0 ${
                      i === 0 ? 'italic text-[20px] leading-[1.5]' : ''
                    }`}
                    style={{ textWrap: 'pretty' }}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {upcomingSessions.length > 0 && (
        <section className="py-8 pb-24">
          <div className="page-container">
            <div className="mb-7 pb-[18px] border-b border-line">
              <h2 className="section-title">
                Upcoming with {firstName}.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingSessions.map((session) => {
                const eventData: EventCardData = {
                  code: session.room.code,
                  name: session.room.name,
                  description: session.room.description,
                  scheduledAt: session.room.scheduledAt,
                  endsAt: session.room.endsAt,
                  startedAt: session.room.startedAt,
                  coverImageUrl: session.room.imageUrl,
                  hostName: session.room.hostName,
                  hostAvatarUrl: session.room.hostAvatarUrl,
                  modelName: profile.isModel ? profile.displayName : null,
                  modelAvatarUrl: profile.isModel ? profile.photos[0]?.url || null : null,
                  isTutored: session.room.isTutored,
                  seatsTotal: session.room.maxAttendees,
                  seatsConfirmed: session.room.seatsConfirmed,
                  pricingType: session.room.pricingType,
                  ticketPrice: session.room.ticketPrice,
                };
                return (
                  <EventCard
                    key={session.room.id}
                    event={eventData}
                    onClick={() => navigate(`/s/${session.room.code}`)}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      {showReferencePacks && (
        <section className="py-8 pb-16">
          <div className="page-container">
            <div className="mb-7 pb-[18px] border-b border-line">
              <h2 className="section-title">
                Reference packs.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {referencePacks.map((pack) => (
                <ReferencePackCard
                  key={pack.id}
                  href={`/s/${pack.room.code}/summary`}
                  pack={{
                    title: pack.title,
                    description: pack.description,
                    priceCents: pack.priceCents,
                    currency: pack.currency,
                    coverImageUrl: pack.coverImageUrl,
                    photoCount: pack.captureIds.length,
                    videoCount: pack.recordingIds.length,
                    license: pack.license,
                    accessDurationLabel: getReferencePackAccessDurationLabel(pack.accessDurationCount, pack.accessDurationUnit),
                    accessExpiresOn: pack.accessExpiresOn,
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {showArtwork && (
        <section className="py-8 pb-16">
          <div className="page-container">
            <div className="flex items-baseline justify-between mb-8 pb-[18px] border-b border-line gap-5">
              <h2 className="section-title">
                Artwork.
              </h2>
            </div>

            <PhotoGallery photos={profile.artworkPhotos} />
          </div>
        </section>
      )}

      {showPortraits && (
        <section className="py-8 pb-16">
          <div className="page-container">
            <div className="flex items-baseline justify-between mb-8 pb-[18px] border-b border-line gap-5">
              <h2 className="section-title">
                Portraits.
              </h2>
            </div>

            <PhotoGallery photos={profile.photos} />
          </div>
        </section>
      )}

      {showPoseSpace && (
        <section className="py-24 border-t border-line">
          <div className="page-container">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-20 items-end mb-10">
              <div>
                <div className="text-[11px] uppercase text-text-dim mb-2.5">
                  Where {firstName} poses
                </div>
                <h2 className="font-display text-[clamp(34px,4vw,56px)] font-light leading-[1.02]">
                  The space.
                </h2>
              </div>
              {profile.cameraEquipment && (
                <p className="text-[18px] leading-[1.55] text-text-dim max-w-[540px]" style={{ textWrap: 'pretty' }}>
                  {profile.cameraEquipment}
                </p>
              )}
            </div>

            {profile.spacePhotos[0] && (
              <Gallery withCaption={false}>
                <Item
                  original={profile.spacePhotos[0].url}
                  thumbnail={profile.spacePhotos[0].url}
                  width={1600}
                  height={800}
                >
                  {({ ref, open }) => (
                    <div
                      ref={ref}
                      onClick={open}
                      className="w-full aspect-[16/8] bg-cover bg-center rounded-lg mb-3.5 cursor-pointer hover:brightness-95 transition-all"
                      style={{ backgroundImage: `url(${profile.spacePhotos[0].url})` }}
                    />
                  )}
                </Item>
              </Gallery>
            )}

            {profile.spacePhotos.length > 1 && (
              <Gallery withCaption={false}>
                <div className="grid grid-cols-12 gap-3.5 mb-14">
                  {profile.spacePhotos.slice(1, 4).map((photo) => (
                    <Item
                      key={photo.id}
                      original={photo.url}
                      thumbnail={photo.url}
                      width={800}
                      height={1000}
                    >
                      {({ ref, open }) => (
                        <div
                          ref={ref}
                          onClick={open}
                          className="col-span-4 max-md:col-span-12 aspect-[4/5] bg-cover bg-center rounded-lg cursor-pointer hover:brightness-95 transition-all"
                          style={{ backgroundImage: `url(${photo.url})` }}
                        />
                      )}
                    </Item>
                  ))}
                </div>
              </Gallery>
            )}
          </div>
        </section>
      )}

      <SharedFooter />
    </div>
  );
}

function PhotoGallery({ photos }: { photos: ProfilePhoto[] }) {
  const [imageDimensions, setImageDimensions] = useState<Record<string, { width: number; height: number }>>({});

  // Load image dimensions
  useEffect(() => {
    photos.forEach((photo) => {
      if (!imageDimensions[photo.id]) {
        const img = new Image();
        img.onload = () => {
          setImageDimensions((prev) => ({
            ...prev,
            [photo.id]: { width: img.naturalWidth, height: img.naturalHeight },
          }));
        };
        img.src = photo.url;
      }
    });
  }, [photos]);

  // Build photo album data with dimensions
  const albumPhotos = photos.map((photo) => {
    const dims = imageDimensions[photo.id] || { width: 4, height: 5 };
    return {
      src: photo.url,
      width: dims.width,
      height: dims.height,
      key: photo.id,
    };
  });

  return (
    <Gallery withCaption={false}>
      <RowsPhotoAlbum
        photos={albumPhotos}
        targetRowHeight={280}
        spacing={12}
        rowConstraints={{ maxPhotos: 4 }}
        render={{
          image: (props, { photo, index }) => (
            <Item
              key={photo.key}
              original={photo.src}
              thumbnail={photo.src}
              width={photo.width}
              height={photo.height}
            >
              {({ ref, open }) => (
                <img
                  ref={ref}
                  {...props}
                  onClick={open}
                  className="cursor-pointer transition-all duration-300 hover:brightness-95"
                  style={{ ...props.style, borderRadius: '8px' }}
                />
              )}
            </Item>
          ),
        }}
      />
    </Gallery>
  );
}
